import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { useBlogPosts, addBlogPost, removeBlogPost, updateBlogPost, StoredBlogPost } from '../../hooks/useBlogPosts';
import { publishToContentful } from '../../lib/contentful';
import { Plus, X, Trash2, CheckCircle2, AlertCircle, Image, FileText, Loader2, Eye, EyeOff, Edit3, Upload } from 'lucide-react';
import { fileToDataUrl } from '../../hooks/useClients';
// Local UI helpers (same pattern as AdminPage)
const inputClass = 'w-full px-3.5 py-2.5 text-sm bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] transition-colors';
function Field({
  label,
  hint,
  required,
  children
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return <div className="space-y-1.5" data-id="element-35">
      <label className="block text-sm font-medium text-gray-700" data-id="element-36">
        {label}
        {required && <span className="text-[#FF6600] ml-1" data-id="element-37">*</span>}
      </label>
      {hint && <p className="text-xs text-gray-400" data-id="element-38">{hint}</p>}
      {children}
    </div>;
}
function Toggle({
  checked,
  onChange,
  label,
  hint
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
}) {
  return <div className="flex items-center justify-between py-3" data-id="element-39">
      <div data-id="element-40">
        <p className="text-sm font-medium text-gray-700" data-id="element-41">{label}</p>
        {hint && <p className="text-xs text-gray-400 mt-0.5" data-id="element-42">{hint}</p>}
      </div>
      <button type="button" onClick={() => onChange(!checked)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${checked ? 'bg-[#00316B]' : 'bg-gray-200'}`} data-id="element-43">
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${checked ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-44" />
      </button>
    </div>;
}
function slugify(text: string) {
  return text.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
}
const CATEGORIES = ['Strategy', 'Design', 'Development', 'Branding'];
interface FormState {
  title: string;
  slug: string;
  category: string;
  author: string;
  readTime: string;
  excerpt: string;
  image: string;
  body: string;
  published: boolean;
}
const defaultForm: FormState = {
  title: '',
  slug: '',
  category: '',
  author: '',
  readTime: '5 min read',
  excerpt: '',
  image: '',
  body: '',
  published: true
};
export function BlogTab() {
  const {
    allPosts,
    loading,
    refetch
  } = useBlogPosts();
  const [form, setForm] = useState<FormState>(defaultForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [imageMode, setImageMode] = useState<'url' | 'upload'>('upload');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState('');
  // Get comment count per post
  const getCommentCount = (slug: string) => allComments.filter(c => c.postSlug === slug).length;
  // Group comments by post
  const commentsByPost = allComments.reduce((acc, comment) => {
    if (!acc[comment.postSlug]) {
      acc[comment.postSlug] = [];
    }
    acc[comment.postSlug].push(comment);
    return acc;
  }, {} as Record<string, typeof allComments>);
  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const value = e.target.value;
    setForm(prev => {
      const next = {
        ...prev,
        [field]: value
      };
      if (field === 'title') {
        next.slug = slugify(value);
      }
      return next;
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    const today = new Date();
    const dateStr = today.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
    try {
      // Publish to Contentful CMS
      await publishToContentful({
        contentType: 'blogPost',
        title: form.title,
        slug: form.slug,
        content: form.body,
        author: form.author
      });
      // Also save locally for immediate UI
      if (editingId) {
        updateBlogPost(editingId, {
          title: form.title,
          slug: form.slug,
          category: form.category,
          author: form.author,
          readTime: form.readTime,
          excerpt: form.excerpt,
          image: form.image,
          body: form.body,
          published: form.published
        });
      } else {
        addBlogPost({
          title: form.title,
          slug: form.slug,
          category: form.category,
          author: form.author,
          readTime: form.readTime,
          excerpt: form.excerpt,
          image: form.image,
          body: form.body,
          date: dateStr,
          published: form.published
        });
      }
      setStatus('success');
      toast.success('Content Live on CMS!');
      setForm(defaultForm);
      setEditingId(null);
      setTimeout(() => {
        setStatus('idle');
        setShowForm(false);
      }, 2000);
    } catch (err: any) {
      setStatus('error');
      toast.error(err?.message || 'Failed to publish. Please try again.');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };
  const handleEdit = (post: StoredBlogPost) => {
    setForm({
      title: post.title,
      slug: post.slug,
      category: post.category,
      author: post.author,
      readTime: post.readTime,
      excerpt: post.excerpt,
      image: post.image,
      body: post.body,
      published: post.published
    });
    setEditingId(post.id);
    setShowForm(true);
  };
  const handleCancelEdit = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(false);
  };
  const handleDelete = (id: string) => {
    removeBlogPost(id);
    setDeleteConfirm(null);
  };
  const handleCommentDelete = (id: string) => {
    removeComment(id);
    setCommentDeleteConfirm(null);
  };
  return <div className="space-y-6" data-id="element-45">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-46">
        <div data-id="element-47">
          <h3 className="text-lg font-bold text-[#0C0C0C]" data-id="element-48">
            Blog Posts ({allPosts.length} total)
          </h3>
          <p className="text-sm text-gray-400 mt-0.5" data-id="element-49">
            Manage all your blog posts.
          </p>
        </div>
        <Button variant="secondary" onClick={() => {
        if (showForm && editingId) {
          handleCancelEdit();
        } else {
          setShowForm(!showForm);
          setEditingId(null);
          setForm(defaultForm);
        }
      }} leftIcon={showForm ? <X className="w-4 h-4" data-id="element-51" /> : <Plus className="w-4 h-4" data-id="element-52" />} data-id="element-50">
          {showForm ? 'Cancel' : 'New Post'}
        </Button>
      </div>

      {/* Collapsible Form */}
      <AnimatePresence data-id="element-53">
        {showForm && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.25
      }} className="overflow-hidden" data-id="element-54">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6" data-id="element-55">
              {/* Post Info Section */}
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60" data-id="element-56">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-57">
                  Post Info
                </p>
              </div>
              <div className="px-6 py-5 space-y-5" data-id="element-58">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-59">
                  <Field label="Title" required data-id="element-60">
                    <input type="text" className={inputClass} placeholder="e.g. How to Build a Great Website" value={form.title} onChange={set('title')} required data-id="element-61" />
                  </Field>
                  <Field label="Slug" hint="Auto-generated from title" data-id="element-62">
                    <input type="text" className={inputClass} placeholder="e.g. how-to-build-a-great-website" value={form.slug} onChange={set('slug')} data-id="element-63" />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-64">
                  <Field label="Category" required data-id="element-65">
                    <select className={inputClass} value={form.category} onChange={set('category')} required data-id="element-66">
                      <option value="" data-id="element-67">Select a category…</option>
                      {CATEGORIES.map(c => <option key={c} value={c} data-id="element-68">
                          {c}
                        </option>)}
                    </select>
                  </Field>
                  <Field label="Author" required data-id="element-69">
                    <input type="text" className={inputClass} placeholder="e.g. Sarah K." value={form.author} onChange={set('author')} required data-id="element-70" />
                  </Field>
                  <Field label="Read Time" data-id="element-71">
                    <input type="text" className={inputClass} placeholder="e.g. 5 min read" value={form.readTime} onChange={set('readTime')} data-id="element-72" />
                  </Field>
                </div>

                <Field label="Excerpt" required hint="1-2 sentences shown on the blog card" data-id="element-73">
                  <textarea className={`${inputClass} resize-none`} rows={2} placeholder="A brief summary of the article…" value={form.excerpt} onChange={set('excerpt')} required data-id="element-74" />
                </Field>
              </div>

              {/* Media Section */}
              <div className="px-6 py-4 border-t border-b border-gray-100 bg-gray-50/60" data-id="element-75">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-76">
                  Cover Image
                </p>
              </div>
              <div className="px-6 py-5 space-y-5" data-id="element-77">
                {/* Toggle between URL and Upload */}
                <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-lg w-fit" data-id="element-78">
                  <button type="button" onClick={() => {
                setImageMode('url');
                setImageError('');
              }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${imageMode === 'url' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-79">
                    Paste URL
                  </button>
                  <button type="button" onClick={() => {
                setImageMode('upload');
                setImageError('');
              }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${imageMode === 'upload' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-80">
                    Upload Photo
                  </button>
                </div>

                {imageMode === 'url' ? <Field label="Cover Image URL" hint="Direct image URL" data-id="element-81">
                    <input type="url" className={inputClass} placeholder="https://images.unsplash.com/..." value={form.image} onChange={set('image')} data-id="element-82" />
                  </Field> : <Field label="Upload Cover Image" hint="JPG or PNG, max 2MB. Image is stored as embedded data." data-id="element-83">
                    <label htmlFor="blog-cover-upload" className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${form.image && !form.image.startsWith('http') ? 'border-green-300 bg-green-50/30' : 'border-gray-200 bg-gray-50/50 hover:border-[#00316B]/30 hover:bg-[#00316B]/5'}`} data-id="element-84">
                      {uploadingImage ? <div className="flex items-center gap-2 text-gray-400" data-id="element-85">
                          <Loader2 className="w-5 h-5 animate-spin" data-id="element-86" />
                          <span className="text-sm" data-id="element-87">Processing…</span>
                        </div> : form.image && !form.image.startsWith('http') ? <div className="flex flex-col items-center gap-2" data-id="element-88">
                          <img src={form.image} alt="Upload preview" className="h-16 w-auto max-w-[200px] object-contain rounded" data-id="element-89" />
                          <span className="text-xs text-green-600 font-medium" data-id="element-90">
                            Photo uploaded — click to replace
                          </span>
                        </div> : <div className="flex flex-col items-center gap-2 text-gray-400" data-id="element-91">
                          <Upload className="w-6 h-6" data-id="element-92" />
                          <span className="text-sm font-medium" data-id="element-93">
                            Click to upload photo
                          </span>
                          <span className="text-xs" data-id="element-94">JPG or PNG, max 2MB</span>
                        </div>}
                      <input id="blog-cover-upload" type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" className="hidden" onChange={async e => {
                  const file = e.target.files?.[0];
                  setImageError('');
                  if (!file) return;
                  const ACCEPTED = ['image/jpeg', 'image/png'];
                  if (!ACCEPTED.includes(file.type)) {
                    setImageError('Only JPG and PNG files are accepted.');
                    return;
                  }
                  if (file.size > 2 * 1024 * 1024) {
                    setImageError('File is too large. Maximum size is 2MB.');
                    return;
                  }
                  setUploadingImage(true);
                  try {
                    const dataUrl = await fileToDataUrl(file);
                    setForm(prev => ({
                      ...prev,
                      image: dataUrl
                    }));
                  } catch {
                    setImageError('Failed to process image. Please try again.');
                  } finally {
                    setUploadingImage(false);
                  }
                }} data-id="element-95" />
                    </label>
                    {imageError && <p className="text-xs text-red-500 flex items-center gap-1.5 mt-2" data-id="element-96">
                        <AlertCircle className="w-3.5 h-3.5" data-id="element-97" />
                        {imageError}
                      </p>}
                  </Field>}

                {form.image && <div className="rounded-lg overflow-hidden border border-gray-100 aspect-[3/2] bg-gray-50 max-w-md relative group" data-id="element-98">
                    <img src={form.image} alt="Preview" className="w-full h-full object-cover" onError={e => {
                ;
                (e.target as HTMLImageElement).style.display = 'none';
              }} data-id="element-99" />
                    <button type="button" onClick={() => {
                setForm(prev => ({
                  ...prev,
                  image: ''
                }));
                setImageError('');
                const fileInput = document.getElementById('blog-cover-upload') as HTMLInputElement;
                if (fileInput) fileInput.value = '';
              }} className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-lg text-gray-500 hover:text-red-500 hover:bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-all" title="Remove image" data-id="element-100">
                      <X className="w-4 h-4" data-id="element-101" />
                    </button>
                  </div>}

                <div className="flex items-start gap-2 p-3 bg-blue-50/60 rounded-lg border border-blue-100" data-id="element-102">
                  <Image className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" data-id="element-103" />
                  <div className="text-xs text-blue-700 space-y-1" data-id="element-104">
                    <p className="font-medium" data-id="element-105">Image guidelines</p>
                    <ul className="list-disc list-inside text-blue-600 space-y-0.5" data-id="element-106">
                      <li data-id="element-107">
                        Recommended size:{' '}
                        <span className="font-medium" data-id="element-108">1200×800px</span> (3:2
                        ratio, landscape)
                      </li>
                      <li data-id="element-109">
                        Formats: <span className="font-medium" data-id="element-110">JPG or PNG</span>
                      </li>
                      <li data-id="element-111">Use high-quality images for best results</li>
                      <li data-id="element-112">
                        You can paste a URL or upload directly from your device
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Content Section */}
              <div className="px-6 py-4 border-t border-b border-gray-100 bg-gray-50/60" data-id="element-113">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-114">
                  Content
                </p>
              </div>
              <div className="px-6 py-5 space-y-5" data-id="element-115">
                <Field label="Body" hint="Supports markdown. Use ## for headings, **bold**, *italic*" data-id="element-116">
                  <textarea className={`${inputClass} resize-none font-mono text-xs`} rows={10} placeholder="Write your article content here…" value={form.body} onChange={set('body')} data-id="element-117" />
                </Field>
              </div>

              {/* Visibility Section */}
              <div className="px-6 py-4 border-t border-b border-gray-100 bg-gray-50/60" data-id="element-118">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-119">
                  Visibility
                </p>
              </div>
              <div className="px-6 divide-y divide-gray-100" data-id="element-120">
                <Toggle checked={form.published} onChange={v => setForm(p => ({
              ...p,
              published: v
            }))} label="Published" hint="Show this post on the blog page" data-id="element-121" />
              </div>

              {/* Submit */}
              <div className="px-6 py-5 border-t border-gray-100 flex items-center justify-between" data-id="element-122">
                <AnimatePresence data-id="element-123">
                  {status === 'success' && <motion.div initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0
              }} className="flex items-center gap-2 text-green-600 text-sm" data-id="element-124">
                      <CheckCircle2 className="w-4 h-4" data-id="element-125" />
                      {editingId ? 'Post updated!' : 'Post added!'}
                    </motion.div>}
                </AnimatePresence>
                <div className="flex items-center gap-4 ml-auto" data-id="element-126">
                  <button type="button" onClick={handleCancelEdit} className="text-sm text-gray-400 hover:text-gray-600 transition-colors" data-id="element-127">
                    {editingId ? 'Cancel Edit' : 'Clear form'}
                  </button>
                  <Button type="submit" variant="secondary" isLoading={status === 'loading'} disabled={status === 'loading'} data-id="element-128">
                    {status === 'loading' ? 'Publishing…' : editingId ? 'Update Post' : 'Add Post'}
                  </Button>
                </div>
              </div>
            </form>
          </motion.div>}
      </AnimatePresence>

      {/* Posts List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-129">
        {allPosts.map(post => {
        return <motion.div key={post.id} layout initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex gap-4 group" data-id="element-130">
              <div className="w-24 h-24 rounded-lg bg-gray-100 overflow-hidden shrink-0" data-id="element-131">
                {post.image ? <img src={post.image} alt={post.title} className="w-full h-full object-cover" data-id="element-132" /> : <div className="w-full h-full flex items-center justify-center text-gray-400" data-id="element-133">
                    <Image className="w-6 h-6 opacity-50" data-id="element-134" />
                  </div>}
              </div>
              <div className="flex-1 min-w-0 py-1" data-id="element-135">
                <div className="flex items-start justify-between gap-2" data-id="element-136">
                  <h4 className="font-bold text-gray-900 truncate" data-id="element-137">
                    {post.title}
                  </h4>
                  <div className="flex gap-1 shrink-0" data-id="element-138">
                    {post.published ? <span className="bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded font-medium uppercase flex items-center gap-1" data-id="element-139">
                        <Eye className="w-3 h-3" data-id="element-140" />
                        Live
                      </span> : <span className="bg-gray-100 text-gray-600 text-[10px] px-2 py-0.5 rounded font-medium uppercase flex items-center gap-1" data-id="element-141">
                        <EyeOff className="w-3 h-3" data-id="element-142" />
                        Draft
                      </span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1" data-id="element-143">
                  <span className="text-xs text-gray-500" data-id="element-144">{post.category}</span>
                  <span className="text-gray-300" data-id="element-145">•</span>
                  <span className="text-xs text-gray-400" data-id="element-146">{post.date}</span>
                </div>
                <p className="text-xs text-gray-500 mt-2 line-clamp-2" data-id="element-147">
                  {post.excerpt}
                </p>

                <div className="mt-auto pt-3 flex items-center justify-end gap-2" data-id="element-148">
                  <button onClick={() => handleEdit(post)} className="p-1.5 text-gray-400 hover:text-[#00316B] hover:bg-[#00316B]/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100" title="Edit post" data-id="element-149">
                    <Edit3 className="w-4 h-4" data-id="element-150" />
                  </button>
                  {deleteConfirm === post.id ? <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1 shadow-sm" data-id="element-151">
                      <button onClick={() => handleDelete(post.id)} className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600" data-id="element-152">
                        Confirm
                      </button>
                      <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 text-gray-500 text-xs hover:bg-gray-100 rounded" data-id="element-153">
                        Cancel
                      </button>
                    </div> : <button onClick={() => setDeleteConfirm(post.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100" title="Delete post" data-id="element-154">
                      <Trash2 className="w-4 h-4" data-id="element-155" />
                    </button>}
                </div>
              </div>
            </motion.div>;
      })}
      </div>

      <p className="text-center text-xs text-gray-400" data-id="element-156">
        Posts are published to Contentful CMS.
      </p>
    </div>;
}